import { FormEvent, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { deleteGrainConfig, upsertGrainConfig } from "../lib/api";
import type { Godown, GrainLimits } from "../lib/api";

/** Per-godown grain-parameter editor, in the console's design system.
 * A district head sets, for each grain a godown grades, the seven FCI
 * FAQ thresholds a QC agent's sample is checked against. Each grain
 * saves independently (PUT /godowns/:id/grain-configs/:grain), so the
 * wheat tab never touches maize. A godown must keep ≥ 1 grain. */

const LIMIT_FIELDS: { key: keyof GrainLimits; label: string; hint: string }[] =
  [
    { key: "moisture", label: "MOISTURE", hint: "% max" },
    { key: "dd", label: "DD", hint: "damaged / discoloured % max" },
    { key: "b", label: "B", hint: "broken % max" },
    { key: "i", label: "I", hint: "immature % max" },
    { key: "ddbi", label: "DD+B+I", hint: "combined % max" },
    { key: "fm", label: "FM", hint: "foreign matter % max" },
    { key: "idk", label: "IDK", hint: "insect-damaged % max" },
  ];

const DEFAULT_LIMITS: GrainLimits = {
  moisture: 14, dd: 3, b: 3, i: 3, ddbi: 8, fm: 1, idk: 1,
};

const GRAIN_PRESETS = [
  "maize", "wheat", "rice", "paddy", "soybean", "barley", "sorghum", "bajra",
];

export function GrainParams({ godown }: { godown: Godown }) {
  const configs = godown.grain_configs ?? [];
  const grains = configs.map((c) => c.grain_type);
  const [active, setActive] = useState<string>(grains[0] ?? "");
  const [adding, setAdding] = useState(false);
  const [newGrain, setNewGrain] = useState("");

  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ["sh-godowns"] });

  const activeGrain = grains.includes(active) ? active : grains[0] ?? "";
  const activeConfig = configs.find((c) => c.grain_type === activeGrain);

  const addMut = useMutation({
    mutationFn: (g: string) => upsertGrainConfig(godown.id, g, DEFAULT_LIMITS),
    onSuccess: (_d, g) => {
      refresh();
      setActive(g);
      setAdding(false);
      setNewGrain("");
    },
  });

  const submitAdd = (e: FormEvent) => {
    e.preventDefault();
    const g = newGrain.trim().toLowerCase();
    if (!g || addMut.isPending) return;
    if (grains.includes(g)) {
      setActive(g);
      setAdding(false);
      setNewGrain("");
      return;
    }
    addMut.mutate(g);
  };

  const options = GRAIN_PRESETS.filter((g) => !grains.includes(g));

  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="card-head">
        <div>
          <h2>Grain parameters</h2>
          <p>
            Thresholds each sample is graded against, per grain. Saving one
            grain doesn't touch the others.
          </p>
        </div>
        <div className="right">
          {adding ? (
            <form
              onSubmit={submitAdd}
              style={{ display: "flex", gap: 6, alignItems: "center" }}
            >
              <input
                className="search"
                list={`grains-${godown.id}`}
                value={newGrain}
                onChange={(e) => setNewGrain(e.target.value)}
                placeholder="grain type"
                autoFocus
                style={{ width: 140, height: 34 }}
              />
              <datalist id={`grains-${godown.id}`}>
                {options.map((g) => (
                  <option key={g} value={g} />
                ))}
              </datalist>
              <button
                type="submit"
                className="btn btn-gold"
                disabled={addMut.isPending}
              >
                {addMut.isPending ? "Adding…" : "Add"}
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setAdding(false);
                  setNewGrain("");
                }}
              >
                Cancel
              </button>
            </form>
          ) : (
            <button className="btn" onClick={() => setAdding(true)}>
              Add grain
            </button>
          )}
        </div>
      </div>

      <div style={{ padding: "14px 20px 20px" }}>
        {grains.length === 0 ? (
          <div className="empty" style={{ border: 0 }}>
            <b>No grains configured</b>
            <p>Add a grain to set the thresholds samples are graded against.</p>
          </div>
        ) : (
          <>
            <div className="tabs" style={{ marginBottom: 16 }}>
              {grains.map((g) => (
                <button
                  key={g}
                  className={"tab" + (g === activeGrain ? " on" : "")}
                  onClick={() => setActive(g)}
                >
                  {g.toUpperCase()}
                </button>
              ))}
            </div>
            {activeConfig && (
              <GrainEditor
                key={`${godown.id}-${activeGrain}`}
                godownId={godown.id}
                grainType={activeGrain}
                initial={activeConfig.limits}
                canDelete={grains.length > 1}
                onChanged={refresh}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function GrainEditor({
  godownId,
  grainType,
  initial,
  canDelete,
  onChanged,
}: {
  godownId: number;
  grainType: string;
  initial: GrainLimits;
  canDelete: boolean;
  onChanged: () => void;
}) {
  const [vals, setVals] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      LIMIT_FIELDS.map((f) => [f.key, String(initial[f.key] ?? "")]),
    ),
  );

  const saveMut = useMutation({
    mutationFn: () => {
      const limits = {} as GrainLimits;
      for (const f of LIMIT_FIELDS) {
        const n = parseFloat(vals[f.key]);
        limits[f.key] =
          Number.isFinite(n) && n >= 0 ? n : DEFAULT_LIMITS[f.key];
      }
      return upsertGrainConfig(godownId, grainType, limits);
    },
    onSuccess: onChanged,
  });

  const delMut = useMutation({
    mutationFn: () => deleteGrainConfig(godownId, grainType),
    onSuccess: onChanged,
  });

  const dirty = useMemo(
    () =>
      LIMIT_FIELDS.some(
        (f) => parseFloat(vals[f.key]) !== Number(initial[f.key]),
      ),
    [vals, initial],
  );

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!saveMut.isPending) saveMut.mutate();
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
          gap: 12,
        }}
      >
        {LIMIT_FIELDS.map((f) => (
          <div className="field" key={f.key}>
            <label>
              {f.label}
              <span className="hint">{f.hint}</span>
            </label>
            <input
              type="number"
              step="0.1"
              min="0"
              inputMode="decimal"
              value={vals[f.key]}
              onChange={(e) =>
                setVals((v) => ({ ...v, [f.key]: e.target.value }))
              }
            />
          </div>
        ))}
      </div>

      <div
        style={{
          display: "flex",
          gap: 10,
          marginTop: 16,
          alignItems: "center",
        }}
      >
        <button
          type="submit"
          className="btn btn-gold"
          disabled={saveMut.isPending || !dirty}
        >
          {saveMut.isPending
            ? "Saving…"
            : dirty
              ? `Save ${grainType}`
              : "Saved"}
        </button>
        {canDelete && (
          <button
            type="button"
            className="btn"
            disabled={delMut.isPending}
            onClick={() => delMut.mutate()}
          >
            {delMut.isPending ? "Removing…" : "Remove grain"}
          </button>
        )}
        {(saveMut.isError || delMut.isError) && (
          <span className="tray-cap" style={{ color: "#B00020", margin: 0 }}>
            {((saveMut.error || delMut.error) as Error)?.message ||
              "Something went wrong."}
          </span>
        )}
      </div>
    </form>
  );
}
